import { LazyAPIImage } from '@components/common/image'
import { formatTimeByTimezone, TIME_FORMAT_TZ } from '@lib/utils/date.utils'
import CompetitionCta from '@components/competitions/competition-cta'
import Link from 'next/link'
import {
  CompetitionLocation,
  CompetitionOrganizer,
} from '@lib/types/competitions'
import { threeMemberTeamCompetitions } from '@lib/constants/competitions'
import { useRouter } from 'next/router'

type Props = {
  organizer: CompetitionOrganizer
  title: string
  startDate: string
  endDate: string
  slug: string
  timezone: string
  location: CompetitionLocation
  isUpcoming: boolean
  isOngoing: boolean
  isLoggedIn: boolean
  hasEnded: boolean
  isRegistered: boolean
  isSubmissionDone: boolean
  isShowCTA?: boolean
  handleRegisterNow: () => void
}

const CompetitionHeader = ({
  organizer,
  title,
  startDate,
  endDate,
  slug,
  timezone,
  location,
  isUpcoming,
  isOngoing,
  isLoggedIn,
  hasEnded,
  isRegistered,
  handleRegisterNow,
  isSubmissionDone,
  isShowCTA = true,
}: Props) => {
  const router = useRouter()
  return (
    <>
      <div
        className="z-20 flex border shadow-sm bg-grey-100 border-grey-400"
        id="normalHeader"
      >
        <div className="container flex-wrap p-4 mx-auto md:hidden">
          <div className="flex items-center my-3">
            <LazyAPIImage
              className="self-center object-contain w-16 h-16 ml-5 bg-white border rounded text-grey-500"
              src={organizer.logo}
              alt={organizer.title}
            />
            <div className="ml-3">
              <Link
                href={{
                  pathname: `/competitions/${slug}`,
                  query: router.query,
                }}
              >
                <a className="text-xl font-600 text-orange">{title}</a>
              </Link>
              <p className="text-grey">
                Hosted by{' '}
                <a
                  className="font-600 hover:underline"
                  href={organizer.website}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {organizer.title}
                </a>
              </p>
            </div>
          </div>
          <div className="flex flex-col flex-wrap justify-between w-full mt-3">
            <div className="mx-5 my-2 text-grey-800">
              STARTS AT:
              <p className="text-grey font-600">
                {formatTimeByTimezone(startDate, timezone, TIME_FORMAT_TZ)}
              </p>
            </div>
            <div className="mx-5 my-2 text-grey-800">
              ENDS AT:
              <p className="text-grey font-600">
                {formatTimeByTimezone(endDate, timezone, TIME_FORMAT_TZ)}
              </p>
            </div>
            <div className="mx-5 my-2 text-grey-800">
              TEAM SIZE:
              <p className="text-grey font-600">
                {threeMemberTeamCompetitions.includes(slug)
                  ? `Upto 3 members`
                  : `1 member`}
              </p>
            </div>
            <div className="mx-5 my-2 text-grey-800">
              LOCATION:
              <p className="text-grey font-600">
                {location.locationType === 'VirtualLocation'
                  ? 'Online'
                  : location.postalAddress}
              </p>
            </div>
            {isShowCTA && (
              <div className="mx-5 mb-5">
                <CompetitionCta
                  loggedIn={isLoggedIn}
                  isUpcoming={isUpcoming}
                  isOngoing={isOngoing}
                  ended={hasEnded}
                  isRegistered={isRegistered}
                  handleRegisterNow={handleRegisterNow}
                  isSubmissionDone={isSubmissionDone}
                  slug={slug}
                />
              </div>
            )}
          </div>
        </div>
        <div className="container flex-wrap hidden px-10 mx-auto md:flex">
          <div className="flex justify-between w-full py-5">
            <div className="flex w-full">
              <LazyAPIImage
                className="self-center object-contain w-40 h-40 p-2 mx-0 my-2 bg-white border rounded text-grey-500"
                src={organizer.logo}
                alt={organizer.title}
              />

              <div className="flex flex-col w-full mx-8 my-3">
                <Link
                  href={{
                    pathname: `/competitions/${slug}`,
                    query: router.query,
                  }}
                >
                  <a className="text-3xl font-600 text-orange">{title}</a>
                </Link>
                <p className="text-grey">
                  Hosted by{' '}
                  <a
                    className="font-600 hover:underline"
                    href={organizer.website}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {organizer.title}
                  </a>
                </p>
                <div className="flex w-full">
                  <div className="flex flex-wrap items-center justify-between w-3/4 mt-3">
                    <div className="my-2 text-grey-800">
                      STARTS AT:
                      <p className="text-grey font-600">
                        {formatTimeByTimezone(
                          startDate,
                          timezone,
                          TIME_FORMAT_TZ
                        )}
                      </p>
                    </div>
                    <div className="hidden h-full border text-grey-300 lg:block" />
                    <div className="my-2 text-grey-800">
                      ENDS AT:
                      <p className="text-grey font-600">
                        {formatTimeByTimezone(
                          endDate,
                          timezone,
                          TIME_FORMAT_TZ
                        )}
                      </p>
                    </div>
                    <div className="hidden h-full border text-grey-300 lg:block" />
                    <div className="my-2 text-grey-800">
                      TEAM SIZE:
                      <p className="text-grey font-600">
                        {threeMemberTeamCompetitions.includes(slug)
                          ? 'Upto 3 members'
                          : '1 member'}
                      </p>
                    </div>
                    <div className="hidden h-full border text-grey-300 lg:block" />
                  </div>
                  {isShowCTA && (
                    <div className="self-center w-56 ml-8">
                      <CompetitionCta
                        loggedIn={isLoggedIn}
                        isUpcoming={isUpcoming}
                        isOngoing={isOngoing}
                        ended={hasEnded}
                        isRegistered={isRegistered}
                        handleRegisterNow={handleRegisterNow}
                        isSubmissionDone={isSubmissionDone}
                        slug={slug}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default CompetitionHeader
